import React, { useState, useCallback, useEffect } from 'react';
import TopNav from '../layouts/TopNav';
import { ToastContainer, toast } from 'react-toastify';
import ReLoginModal from '../components/ReLoginModal';
import { CircularProgress, LinearProgress } from '@mui/material';
import Swal from 'sweetalert2';
import LicenseSelectModal from '../components/Modals/selectModal/LicenceSelectModal';
import { useNavigate } from 'react-router-dom';
import api from '../util/axios';

interface LicenseFields {
  licenseNumber: string;
  licenseId: string;
  licenseOwner: string;
  destination: string;
  issuedDate: string;
  expiryDate: string;
  documentUrl: string;
}
// interface Customer {
//   _id: string;
//   businessName: string;
// }

const RegisterLicense: React.FC = () => {
  const navigate = useNavigate();
  const [reLogin, setReLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState<File | null>(null); // Store the File object
  const [fileUrl, setFileUrl] = useState<string | null>(null); // For local preview
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null); // For API-uploaded URL
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [fields, setFields] = useState<LicenseFields>({
    licenseNumber: '',
    licenseId: '',
    licenseOwner: '',
    destination: '',
    issuedDate: '',
    expiryDate: '',
    documentUrl: '',
  });
  // console.log("fields", fields);
  // const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const today = new Date();
  const formattedToday = today.toISOString().split('T')[0]; // Format date as YYYY-MM-DD

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFileName(selectedFile.name);
      setFile(selectedFile); // Store the File object
      setFileUrl(URL.createObjectURL(selectedFile)); // For local preview
    }
  }, []);

  const clearFile = useCallback(() => {
    setFileName('');
    setFile(null);
    setFileUrl(null);
    setUploadedUrl(null);
    setFields((prev) => ({ ...prev, documentUrl: '' }));
    const fileInput = document.getElementById('uploadDoc') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }, []);

  const returnToMenu = () => {
    setIsClosing(true);
    navigate(-1);
  };

  const handleSelectLicense = useCallback(
    (licenseData: {
      _id: string;
      licenseNumber: string;
      licenseOwner: string;
      destination: string;
      documentUrl?: string;
      programId?: string;
      truckNumber?: string;
      quantity?: string;
      product?: { id: string; name: string };
      exDepotPrice?: string;
      issuedDate?: string;
      expiryDate?: string;
    }) => {
      const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0];
      };
      // console.log("licenseData:", licenseData);

      setFields({
        licenseNumber: licenseData.licenseNumber,
        licenseId: licenseData._id,
        licenseOwner: licenseData.licenseOwner,
        destination: licenseData.destination,
        issuedDate: formatDate(licenseData.issuedDate),
        expiryDate: formatDate(licenseData.expiryDate),
        documentUrl: licenseData.documentUrl || '',
      });
      // setSelectedCustomer({ _id: licenseData.customerId, businessName: licenseData.businessName });
      setUploadedUrl(licenseData.documentUrl || null);
      setFileUrl(licenseData.documentUrl || null);
      setIsLicenseModalOpen(false);
    },
    []
  );

  const imageFileUpload = useCallback(async () => {
    if (!file) {
      toast.error('No file selected for upload.');
      return null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setReLogin(true);
      toast.error('Session expired, please log in again.');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post(
        '/upload-image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const imageUrl = response.data.data; // Adjust based on your API response structure
      // console.log('File uploaded successfully, URL:', imageUrl);
      return imageUrl;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to upload image.';
      toast.error(errorMessage);
      console.error('Upload failed:', error);
      return null;
    }
  }, [file]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired, please log in again.');
        setReLogin(true);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);

      try {
        // Upload image first if a file is selected
        let documentUrl = uploadedUrl || '';
        if (file) {
          const uploadedImageUrl = await imageFileUpload();
          if (!uploadedImageUrl) {
            setIsLoading(false);
            return; // Stop if image upload fails
          }
          documentUrl = uploadedImageUrl;
          setUploadedUrl(uploadedImageUrl); // Store the uploaded URL
          setFields((prev) => ({ ...prev, documentUrl: uploadedImageUrl }));
        }

        const response = await api.post(
          '/license',
          {
            licenseNumber: fields.licenseNumber,
            licenseOwner: fields.licenseOwner,
            destination: fields.destination,
            issuedDate: fields.issuedDate,
            expiryDate: fields.expiryDate,
            documentUrl,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        Swal.fire({
          title: 'Success',
          text: response.data.message,
          icon: 'success',
          confirmButtonColor: 'green',
          confirmButtonText: 'OK',
        });

        toast.success('License registered successfully!');
        setFields({
          licenseNumber: '',
          licenseId: '',
          licenseOwner: '',
          destination: '',
          issuedDate: '',
          expiryDate: '',
          documentUrl: '',
        });
        // setSelectedCustomer(null);
        setFileName('');
        setFile(null);
        setFileUrl(null);
        setUploadedUrl(null);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || error.message || 'An error occurred while registering the license.';
        if (errorMessage.includes('jwt expired') || errorMessage.includes('jwt malformed')) {
          toast.error('Session expired, please log in again.');
          setReLogin(true);
        } else {
          Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error',
            confirmButtonColor: 'red',
            confirmButtonText: 'OK',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fields, file, uploadedUrl]
  );
  const expired = fields.expiryDate && fields.expiryDate < formattedToday && fields.licenseId;

  useEffect(() => {
    if (expired) {
      Swal.fire({
        title: 'Warning',
        text: 'This license has expired.',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK',
      });
    }
  }, [expired]);


  return (
    <div className="registerLicenseBody h-screen">
      <ToastContainer />
      <TopNav title="Register Outlet Retail License" />
      <div className="registerLicenseContainer flex justify-between my-8 mx-8 bg-white shadow-md h-5/6">
        <div className="licenseForm w-6/12 p-8">
          <form onSubmit={handleSubmit}>
            <div className="license">
              <div className="licenseInput my-2 flex items-center">
                <label>License Number: </label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={fields.licenseNumber}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="ml-2 outline-none cursor-pointer bg-gray-200 px-2 py-1 rounded"
                  onClick={() => setIsLicenseModalOpen(true)}
                >
                  ...
                </button>
              </div>
              <div className="licenseInput my-2 flex items-center">
                <label>License Owner: </label>
                <input
                  type="text"
                  name="licenseOwner"
                  value={fields.licenseOwner}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="licenseInput my-2 flex items-center">
                <label>Destination: </label>
                <input
                  type="textarea"
                  name="destination"
                  value={fields.destination}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="licenseInput my-2 flex items-center">
                <label>Issued Date: </label>
                <input
                  type="date"
                  name="issuedDate"
                  value={fields.issuedDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="licenseInput my-2 flex items-center">
                <label>Expiry Date: </label>
                <input
                  type="date"
                  name="expiryDate"
                  value={fields.expiryDate}
                  onChange={handleChange}
                  required
                />
              </div>
              {expired && (
                <p className='text-center text-red-500 text-sm'>Document has expired</p>
              )}
            </div>
            <div className="document">
              <div className="licenseFileInput">
                <label htmlFor="uploadDoc" style={{ cursor: 'pointer' }}>
                  Upload Document
                </label>
                <input
                  id="uploadDoc"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {fileName && (
                  <>
                    <p className="text-green-900">
                      <strong>Selected File</strong>: {fileName}
                    </p>
                    <button
                      type="button"
                      className="bg-red-500 text-white px-2 py-1 rounded-md mt-2"
                      onClick={clearFile}
                    >
                      Remove File
                    </button>
                  </>
                )}
              </div>
              <span>Document size limit: 20mb</span>
              <div className="viewDocument1">
                <button
                  type="button"
                  className="viewDocumentBtn"
                  onClick={() => {
                    if (fileUrl) {
                      window.open(fileUrl, '_blank');
                    } else {
                      toast.error('No document available to view.');
                    }
                  }}
                >
                  View Document in Browser
                </button>
              </div>
            </div>
            <div className="licenseActions">
              <div className="licenseFormButtons flex gap-8 mt-4 w-full">
                <button type="submit" className="saveBtn" disabled={isLoading}>
                  {isLoading ? (
                    <CircularProgress
                      style={{ color: '#fff', width: '25px', height: '25px' }}
                    />
                  ) : (
                    'Save'
                  )}
                </button>
                <button onClick={returnToMenu} type="button" className="formCancelBtn">
                  {isClosing ? <CircularProgress size={30} /> : 'Cancel'}
                </button>
              </div>
            </div>
          </form>
        </div>
        <div className="licenseDocument w-6/12 flex justify-center">
          {fileUrl ? (
            <img src={fileUrl} alt={`document upload ${fileName}`} />
          ) : (
            <div className="tempDoc mt-16">
              <p className='bg-blue-400 p-4 rounded-lg text-gray-200'>No document available</p>
            </div>
          )}
        </div>
      </div>
      {reLogin && <ReLoginModal />}
      <LicenseSelectModal
        open={isLicenseModalOpen}
        onClose={() => setIsLicenseModalOpen(false)}
        onSelect={handleSelectLicense}
      />
      {/*
      <CustomerModal
        open={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSelect={handleSelectCustomer}
      />*/}
    </div>
  );
};

export default RegisterLicense;